import React, { forwardRef } from 'react';
import type { InputBaseProps as MuiInputBaseProps } from '@mui/material';
import { InputBase as MuiInputBase } from '@mui/material';
import { isEmpty } from 'lodash';

import { reportErrorInProductionOrThrow } from '~/Utils';

/**
 * This Component is a wrapper on MUI component.
 * This component should be imported and not MUI directly!
 *
 * Explicit InputBase props based on actual usage in the codebase.
 */
export interface InputBaseProps {
  /** If `true`, the `input` element is focused during the first mount. */
  autoFocus?: MuiInputBaseProps['autoFocus'];
  /** Override or extend the styles applied to the component. */
  classes?: MuiInputBaseProps['classes'];
  /** If `true`, the `input` element is disabled. */
  disabled?: MuiInputBaseProps['disabled'];
  /** End `InputAdornment` for this component. */
  endAdornment?: MuiInputBaseProps['endAdornment'];
  /** If `true`, the input takes up the full width of its container. */
  fullWidth?: MuiInputBaseProps['fullWidth'];
  /** Attributes applied to the underlying `input` element. */
  inputProps?: MuiInputBaseProps['inputProps'];
  /** Pass a ref to the underlying `input` element. */
  inputRef?: MuiInputBaseProps['inputRef'];
  /** If `true`, a `textarea` element is rendered instead of an input. */
  multiline?: MuiInputBaseProps['multiline'];
  /** Callback fired when the input value is changed. */
  onChange?: MuiInputBaseProps['onChange'];
  /** Callback fired on key down. */
  onKeyDown?: MuiInputBaseProps['onKeyDown'];
  /** The short hint displayed in the input before the user enters a value. */
  placeholder?: MuiInputBaseProps['placeholder'];
  /** The value of the `input` element. */
  value?: MuiInputBaseProps['value'];
}

const InputBase = forwardRef<HTMLDivElement, InputBaseProps>(
  (
    {
      autoFocus,
      classes,
      disabled,
      endAdornment,
      fullWidth,
      inputProps,
      inputRef,
      multiline,
      onChange,
      onKeyDown,
      placeholder,
      value,
      ...restProps
    },
    ref
  ) => {
    if (!isEmpty(restProps)) {
      reportErrorInProductionOrThrow(
        `Unexpected props passed to InputBase: ${Object.keys(restProps).join(', ')}.
Examine if these need to be removed, or added explicitly to the InputBaseProps interface and to the component's signature.`,
        true
      );
    }
    return (
      <MuiInputBase
        autoFocus={autoFocus}
        classes={classes}
        disabled={disabled}
        endAdornment={endAdornment}
        fullWidth={fullWidth}
        inputProps={inputProps}
        inputRef={inputRef}
        multiline={multiline}
        onChange={onChange}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        value={value}
        ref={ref}
        {...restProps}
      />
    );
  }
);

InputBase.displayName = 'InputBase';

export default InputBase;
